import PageLoader from '@/app/common/components/PageLoader'
import { useGetProgramCatalogCategories } from '@/queries/program_catalog/hooks/useGetProgramCatalogCategories'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import { ProviderItem } from '@/queries/providers/schema'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import ProductForm from '../ProductForm/ProductForm'
import {
  ConfigureStoreEditValues,
  numberToBoolean,
  ProductDurationNumberToStringSchema,
  TransformedProduct,
} from '../ProductForm/add-product.schema'
import { useUpdateProduct } from '../configureStore/hooks/useUpdateProduct'
import EditProductDialog from './EditProductSuccessAlert'

type Props = {
  product: ProviderItem
  refetch: () => void
}

export default function EditProduct(props: Props) {
  const { product, refetch } = props

  const [isOpen, setIsOpen] = useState(false)
  const { id } = useSelector(selectSelectedProgram)

  const params = useParams()
  const providerId = params.providerId ? parseInt(params.providerId) : undefined
  const systemCategoriesQuery = useGetProgramCatalogCategories(id)
  const { toast } = useToast()
  if (!providerId) {
    throw new Error('Provider is is not provided in the URL parameters.')
  }

  const editProductMutation = useUpdateProduct({
    providerId: product.provider_id,
    programId: id,
  })

  // Map ProviderItem to ConfigureStoreEditValues (moved from Marketplace)
  const parsedSpecificDuration = ProductDurationNumberToStringSchema.safeParse(
    product.specific_duration
  )
  const salesTaxNumber = Number(product.sales_tax)
  const sales_tax = isNaN(salesTaxNumber) ? undefined : salesTaxNumber

  const initialValues: ConfigureStoreEditValues = {
    name: product.name,
    program_item_id: product.program_item_id.toString(),
    global_item_category_id: product.global_item_category_id.toString(),
    global_item_id: product.global_item_id.toString(),
    price: product.price.toString(),
    product_type: typeof product.type === 'number' ? product.type : 1,
    comments: product.comments || '',
    keyword_tags: product.keyword_tags
      ? Array.isArray(product.keyword_tags)
        ? product.keyword_tags
        : product.keyword_tags.split(',')
      : [],
    saved_image_path: product.image || undefined,
    description: product.comments || '',
    usage_quantity: product.usage_quantity || undefined,
    duration_type: parsedSpecificDuration.success
      ? parsedSpecificDuration.data
      : 'period',
    duration_unit: product.duration_unit ? product.duration_unit : undefined,
    specific_duration: product.specific_duration
      ? product.specific_duration.toString()
      : undefined,
    start_date: product.start_date ? product.start_date : undefined,
    end_date: product.end_date ? product.end_date : undefined,
    is_subscription: product.subscription
      ? numberToBoolean(product.subscription)
      : false,
    marketplace: product.marketplace,
    sales_tax,
    type: product?.type ? Number.parseInt(product.type.toString()) : 0,
  }

  function handleSubmitProductForm(
    data: TransformedProduct,
    categories: ProgramCategory[]
  ) {
    let selectedProgramItem:
      | ProgramCategory['program_items'][number]
      | undefined
    let parentCategory: ProgramCategory | undefined
    for (const category of categories) {
      const found = category.program_items.find(
        (item) => item.id.toString() === data.program_item_id?.toString()
      )
      if (found) {
        selectedProgramItem = found
        parentCategory = category
        break
      }
    }

    if (!selectedProgramItem || !parentCategory) {
      toast({
        title: 'Error',
        description: 'There was an error creating your product.',
        variant: 'destructive',
      })
      return
    }

    const payload = { ...data }
    if (payload.type !== 1) {
      delete payload.duration_type
      delete payload.duration_unit
      delete payload.specific_duration
      delete payload.start_date
      delete payload.end_date
    }

    console.log(payload, 'payload')
    editProductMutation.mutate(
      {
        item_id: product.id,
        payload: payload,
      },
      {
        onSuccess: () => {
          refetch()
          toast({
            variant: 'default',
            title: 'Success',
            description: 'Product edited successfully.',
          })
          setIsOpen(true)
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'There was an error adding the product',
          })
        },
      }
    )
  }

  return (
    <div className="max-w-4xl w-full mx-auto">
      <PageLoader query={systemCategoriesQuery.query}>
        {(categories) => {
          return (
            <>
              <ProductForm
                isNewProduct={false}
                initialValues={initialValues}
                mutationStatus={editProductMutation.status}
                onSubmit={(data) => {
                  handleSubmitProductForm(data, categories)
                }}
                categories={categories}
              />

              {editProductMutation.status === 'success' && (
                <EditProductDialog
                  open={isOpen}
                  programId={id}
                  providerId={providerId}
                  onOkClick={() => {
                    setIsOpen(false)
                  }}
                  marketplaceStatus={editProductMutation.data.marketplace}
                />
              )}
            </>
          )
        }}
      </PageLoader>
    </div>
  )
}
